/**
 * Validation and normalization for the link URLs users type into the task
 * dialog. Shared by the task-level and sub-task-level link forms so both
 * accept exactly the same inputs.
 */

/**
 * Returns an error message for a URL the user typed, or null when it is
 * acceptable. An empty string is treated as acceptable — the caller decides
 * whether a link is required.
 */
export const validateUrl = (url: string): string | null => {
  const trimmed = url.trim();
  if (!trimmed) return null;

  // Check if URL has a protocol
  if (/^https?:\/\//.test(trimmed)) {
    try {
      new URL(trimmed);
      return null;
    } catch {
      return "Invalid URL format";
    }
  }

  // Allow URLs without protocol if they contain a dot or are localhost
  if (
    /^localhost(:\d+)?/.test(trimmed) ||
    /^127\.0\.0\.1(:\d+)?/.test(trimmed)
  ) {
    return null;
  }

  if (/\./.test(trimmed)) {
    return null;
  }

  return "URL must contain a domain (e.g., example.com or https://example.com)";
};

/** Adds `https://` to a URL the user typed without a protocol. */
export const normalizeUrl = (url: string): string => {
  const trimmed = url.trim();
  // If it already has a protocol, return as is
  if (/^https?:\/\//.test(trimmed)) {
    return trimmed;
  }
  // Add https:// to URLs without protocol
  return `https://${trimmed}`;
};
